'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function LiposuctionZayiflamaYontemiMidirGerceklerVeYanilgilarPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/liposuction-zayiflama-yontemi-midir-gercekler-ve-yanilgilar';
  const pageTitle = 'Liposuction Zayıflama Yöntemi midir? Gerçekler ve Yanılgılar';
  const pageDescription = 'Liposuction kilo verme yöntemi midir? Yağ aldırma işlemi zayıflatır mı yoksa sadece şekillendirir mi? Liposuction hakkında doğru bilinen yanlışlar.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Liposuction Zayıflama Yöntemi midir?', href: '/tr/blog/liposuction-zayiflama-yontemi-midir-gercekler-ve-yanilgilar' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Liposuction zayıflama yöntemi mi vücut şekillendirme mi değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Liposuction
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Liposuction Zayıflama Yöntemi midir? Gerçekler ve Yanılgılar
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>3 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Liposuction (yağ aldırma işlemi), uzun yıllardır vücut şekillendirme amacıyla yapılan en popüler cerrahi estetik işlemlerden biridir. Ancak halk arasında sıkça "zayıflama yöntemi" olarak yanlış şekilde tanımlanır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Oysa liposuction, kilo verme işlemi değil, vücut hatlarını yeniden dengeleme operasyonudur. Bu yazıda liposuction'ın ne işe yaradığını, kimler için uygun olduğunu ve neden "zayıflama yöntemi" olarak görülmemesi gerektiğini detaylı şekilde açıklıyoruz.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Liposuction Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Liposuction, cilt altındaki yağ hücrelerinin vakum yöntemiyle alınması işlemidir. Amaç; fazla yağ birikmiş bölgelerde (karın, bel, kalça, bacak, kol gibi) vücut konturunu düzeltmek ve orantılı bir görünüm sağlamaktır.
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <p className="text-red-800 font-medium">
                  💡 Önemli: Liposuction kilo düşürmez — yalnızca yağ hücresi sayısını azaltır. Yani terazi üzerindeki rakam değil, ayna karşısındaki şekil değişir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Liposuction Zayıflatır mı?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hayır, liposuction bir zayıflama yöntemi değildir. Kilo, vücut genelindeki yağ ve kas oranının toplamıdır. Liposuction sadece belirli bölgelerdeki yağ fazlasını azaltır ama genel vücut ağırlığını ciddi ölçüde değiştirmez.
              </p>

              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <p className="text-orange-800 font-medium">
                  👉 Ortalama bir liposuction işleminde alınan yağ miktarı: 2–4 litre (yaklaşık 2–3 kg) arasındadır.
                </p>
                <p className="text-orange-800 mt-2">
                  Bu nedenle, işlem sonrasında kişi daha fit görünür ama kilosu büyük oranda aynı kalır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Liposuction Ne İçin Yapılır?
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Amaç
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Vücut şekillendirme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Bel, karın, kalça gibi orantısız bölgelerin inceltilmesi</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Yağ dağılımını dengeleme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Bölgesel fazlalıkların giderilmesi</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Giysi uyumunu artırma</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Vücut hatlarını estetik hale getirme</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kas hatlarını belirginleştirme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Fit görünüm elde etme (özellikle "liposculpture" tekniklerinde)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 font-medium">
                Dolayısıyla liposuction bir şekillendirme operasyonudur, diyet alternatifi değildir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Liposuction Öncesi ve Sonrası
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dönem
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Öncesi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kişi ideal kilosuna yakın olmalıdır. Aşırı kilolu kişiler için uygun değildir.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İşlem</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Lokal veya genel anesteziyle yapılır, bölgeye göre 1–3 saat sürebilir.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Sonrası</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Ödem ve morluk birkaç hafta içinde azalır, sonuçlar 2–3 ayda netleşir.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kalıcılık</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yağ hücreleri alınan bölgede yeniden oluşmaz, ancak kilo alınırsa kalan hücreler büyüyebilir.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 mb-8">
                <p className="text-blue-800 font-medium">
                  💡 Liposuction sonrası sağlıklı beslenme ve düzenli egzersiz kalıcılığı artırır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Liposuction Kimler İçin Uygundur?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">✅ Uygun Adaylar:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-2">
                    <li>İdeal kilosuna yakın olan, ancak bölgesel yağ fazlalığı bulunan kişiler</li>
                    <li>Cilt elastikiyeti iyi olan hastalar</li>
                    <li>Gerçekçi beklentilere sahip bireyler</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">⚠️ Uygun değildir:</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Aşırı obez kişiler</li>
                    <li>Kronik kalp, karaciğer, böbrek hastalığı olanlar</li>
                    <li>Gebeler veya yeni doğum yapmış kadınlar</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Liposuction ve Zayıflama Arasındaki Fark
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Liposuction
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Zayıflama (Diyet / Egzersiz)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Amaç</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Vücut şekillendirme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kilo kaybı</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Sonuç</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Bölgesel incelme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Genel kilo azalması</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Süre</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Anında gözle görülür</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Süreç içinde gerçekleşir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kalıcılık</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yağ hücresi alınan bölgede kalıcı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kilo alınırsa geri döner</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kapsam</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Belirli bölge</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Tüm vücut</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Liposuction zayıflama yöntemi mi vücut şekillendirme mi"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Liposuction kilo vermemi sağlar mı?</h3>
                  <p className="text-gray-700">Hayır. Liposuction vücut şekillendirme işlemidir, kilo verme yöntemi değildir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Liposuction sonrası kilo alırsam sonuç bozulur mu?</h3>
                  <p className="text-gray-700">Evet. Yağ hücresi alınan bölgede azalsa da kalan hücreler büyüyebilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Diyet yapmadan liposuction olur mu?</h3>
                  <p className="text-gray-700">Hayır. Liposuction kalıcı sonuç için diyet ve egzersizle desteklenmelidir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Liposuction obezite tedavisi midir?</h3>
                  <p className="text-gray-700">Hayır. Obezite cerrahisi ile liposuction tamamen farklıdır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. En iyi liposuction sonuçları kimlerde alınır?</h3>
                  <p className="text-gray-700">İdeal kilosuna yakın, ancak bölgesel yağ fazlalığı bulunan hastalarda.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Society of Plastic Surgeons (ASPS) – "Liposuction Overview and Indications"</li>
                <li>Türk Plastik Rekonstrüktif ve Estetik Cerrahi Derneği (TPRECD) – Liposuction Kılavuzu</li>
                <li>Mayo Clinic – "Liposuction: What You Can Expect"</li>
                <li>Harvard Health Publishing – "Liposuction Myths and Facts"</li>
                <li>ISAPS Global Report 2024 – "Body Contouring and Fat Removal Statistics"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">#liposuction</span>
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">#yağaldırma</span>
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">#zayıflama</span>
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">#vücutşekillendirme</span>
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">#estetikcerrahi</span>
                <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">#ameliyatsızfitgörünüm</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Liposuction Hakkında Bilgi Alın</h3>
              <p className="mb-6">Vücut şekillendirme ve liposuction işlemi için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Liposuction hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-orange-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Cerrahi olmayan yüz gençleştirmenin bilimsel açıklaması.</p>
            </Link>
            <Link
              href="/tr/blog/6-nokta-lifting-nedir-yuz-genclestirmede-yeni-nesil-teknik"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">6 Nokta Lifting Nedir?</h4>
              <p className="text-gray-600 text-sm">Yüz gençleştirmede yeni nesil teknik.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
